<template>
  <div>
    <p class="error" v-if="error">{{ error }}</p>
    <a-form class="container" :form="form" @submit="handleSubmit">
      <r-title>{{ $t('signup') }}</r-title>
      <div class="privacy">
        <a-checkbox v-a11y-tab v-model="accept"></a-checkbox>
        <span v-html="$t('accept.terms', { terms, privacyPolicy })"></span>
      </div>
      <full-name-input v-model="fullName" :required="true" />
      <email-input v-model="email" />
      <create-password-input v-model="password" />
      <a-button
        type="primary"
        html-type="submit"
        :disabled="disabled"
        :loading="loading"
      >{{ submitButtonText }}</a-button>
      <login-method-separator :text="$t('social.method.separator.signup')" />
      <o-auth @success="onSuccessRegistration" />
    </a-form>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

import RTitle from '@/components/common/Title.vue';
import EmailInput from '@/components/common/EmailInput.vue';
import FullNameInput from '@/components/common/FullNameInput.vue';
import CreatePasswordInput from '@/components/common/CreatePasswordInput.vue';
import OAuth from '@/components/login/OAuth.vue';
import LoginMethodSeparator from '@/components/login/LoginMethodSeparator.vue';

import { SIGN_UP } from '@/store/session/actionTypes';
import { ROUTER_PATHS } from '@/router/constants';

import { PDF } from '@/constants';

export default {
  props: {
    onSuccess: {
      type: Function,
    },
  },
  components: {
    RTitle,
    FullNameInput,
    EmailInput,
    OAuth,
    LoginMethodSeparator,
    CreatePasswordInput,
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      loading: false,
      error: '',
      accept: false,
      invalid: true,
      terms: PDF.TERM,
      privacyPolicy: PDF.PRIVACY_POLICY,
    };
  },
  beforeCreate() {
    this.form = this.$form.createForm(this, { name: 'signUp' });
  },
  mounted() {
    // FB hash symbol tmp fix
    const expectedUrl = `${window.location.origin}/${window.location.hash}`;
    // eslint-disable-next-line
    const href = window.location.href;

    if (expectedUrl !== href) {
      // this means that we probably have 2 queries in url
      const moreThanOnequerySigns = href.indexOf('?') !== href.lastIndexOf('?');
      if (href.indexOf('?') < href.indexOf('#')) {
        const query = moreThanOnequerySigns
          ? window.location.search.replace('?', '&')
          : window.location.search;

        const newHref = `${expectedUrl}${query}`;
        window.history.replaceState('', '', newHref);
      }
    }
    // end fix
  },
  inject: {
    theme: {
      type: Object,
      default: {},
    },
  },
  computed: {
    fullName: {
      get() {
        return {
          firstName: this.firstName,
          lastName: this.lastName,
        };
      },
      set(value) {
        this.firstName = value.firstName;
        this.lastName = value.lastName;
      },
    },
    disabled() {
      return this.invalid || !this.accept || this.loading;
    },
    submitButtonText() {
      return this.theme?.submitButtonText || this.$t('submit');
    },
  },
  provide() {
    return {
      form: this.form,
    };
  },
  methods: {
    handleSubmit(e) {
      e.preventDefault();
      this.form.validateFieldsAndScroll((err) => {
        if (!err) {
          this.validateAndSave({
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            password: this.password,
          });
        }
      });
    },
    ...mapActions({
      signUp: SIGN_UP,
    }),
    async validateAndSave(data) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      try {
        this.signUp(data);
        this.onSuccessRegistration();
      } catch (error) {
        this.error = this.$t(error.code) || error.message;
      } finally {
        this.loading = false;
      }
    },
    updateDisabled() {
      this.form.validateFields((err) => {
        if (!err) {
          this.invalid = false;
        } else {
          this.invalid = true;
        }
      });
    },
    onSuccessRegistration() {
      if (this.onSuccess) {
        this.onSuccess();
      } else {
        this.$router.push(ROUTER_PATHS.SUCCESS_REGISTRATION);
      }
    },
  },
  watch: {
    firstName() {
      this.updateDisabled();
    },
    lastName() {
      this.updateDisabled();
    },
    email() {
      this.updateDisabled();
    },
    password() {
      this.updateDisabled();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/colors.scss';

.container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.error {
  color: $red;
  text-align: center;
}

.privacy {
  display: flex;

  span {
    margin-left: 15px;
    margin-bottom: 10px;
  }
}
</style>
